/**
 * Pronunciation Chart Component using Recharts
 * Displays pronunciation accuracy trends over time
 */

import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { InfoIcon } from '../SvgConsolidated/SvgIcons';
import { PronunciationTrend, PronunciationAssessment } from './types';

interface PronunciationChartProps {
  trendData: PronunciationTrend[];
  assessments: PronunciationAssessment[];
  type?: 'trend' | 'distribution' | 'words';
  className?: string;
}

export default function PronunciationChart({ 
  trendData, 
  assessments, 
  type = 'trend', 
  className = '' 
}: PronunciationChartProps) {
  
  // Color scheme
  const colors = {
    primary: '#10b981', // green-500
    secondary: '#3b82f6', // blue-500
    warning: '#f59e0b', // amber-500
    danger: '#ef4444', // red-500
    neutral: '#6b7280' // gray-500
  };

  const renderTrendChart = () => (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={trendData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
        <XAxis 
          dataKey="date" 
          stroke="#6b7280"
          fontSize={12}
          tickFormatter={(value) => new Date(value).toLocaleDateString()}
        />
        <YAxis 
          stroke="#6b7280"
          fontSize={12}
          domain={[0, 100]}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}
          formatter={(value: number, name: string) => [
            name === 'averageAccuracy' ? `${value.toFixed(1)}%` : value,
            name === 'averageAccuracy' ? 'Accuracy' : 'Interventions'
          ]}
          labelFormatter={(value) => `Date: ${new Date(value).toLocaleDateString()}`}
        />
        <Legend />
        <Line
          type="monotone"
          dataKey="averageAccuracy"
          stroke={colors.primary}
          strokeWidth={3}
          dot={{ fill: colors.primary, strokeWidth: 2, r: 4 }}
          activeDot={{ r: 6, stroke: colors.primary, strokeWidth: 2 }}
          name="Accuracy %"
        />
        <Line
          type="monotone"
          dataKey="interventions"
          stroke={colors.warning}
          strokeWidth={2}
          dot={{ fill: colors.warning, strokeWidth: 2, r: 3 }}
          name="Interventions"
          yAxisId="right"
        />
      </LineChart>
    </ResponsiveContainer>
  );

  const renderDistributionChart = () => {
    const accuracyBands = [
      { name: '90-100%', value: 0, color: colors.primary },
      { name: '80-89%', value: 0, color: '#059669' },
      { name: '70-79%', value: 0, color: colors.warning },
      { name: '60-69%', value: 0, color: '#dc2626' },
      { name: 'Below 60%', value: 0, color: colors.danger }
    ];

    assessments.forEach(assessment => {
      const score = assessment.accuracyScore;
      if (score >= 90) accuracyBands[0].value++;
      else if (score >= 80) accuracyBands[1].value++;
      else if (score >= 70) accuracyBands[2].value++;
      else if (score >= 60) accuracyBands[3].value++;
      else accuracyBands[4].value++;
    });

    return (
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={accuracyBands}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={(entry: any) => `${entry.name}: ${((entry.value / accuracyBands.reduce((sum, band) => sum + band.value, 0)) * 100).toFixed(1)}%`}
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
          >
            {accuracyBands.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  const renderWordAnalysisChart = () => {
    // Get most challenging words
    const wordStats = assessments.reduce((acc, assessment) => {
      const word = assessment.word.toLowerCase();
      if (!acc[word]) {
        acc[word] = {
          word,
          attempts: 0,
          totalAccuracy: 0,
          interventions: 0
        };
      }
      acc[word].attempts++;
      acc[word].totalAccuracy += assessment.accuracyScore;
      if (assessment.needsIntervention) {
        acc[word].interventions++;
      }
      return acc;
    }, {} as Record<string, any>);

    const wordData = Object.values(wordStats)
      .map((stats: any) => ({
        word: stats.word,
        averageAccuracy: stats.totalAccuracy / stats.attempts,
        attempts: stats.attempts,
        interventionRate: (stats.interventions / stats.attempts) * 100
      }))
      .sort((a, b) => a.averageAccuracy - b.averageAccuracy)
      .slice(0, 10); // Top 10 most challenging words

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={wordData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis 
            dataKey="word" 
            stroke="#6b7280"
            fontSize={12}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis 
            stroke="#6b7280"
            fontSize={12}
            domain={[0, 100]}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}
            formatter={(value: number, name: string) => [
              name === 'averageAccuracy' ? `${value.toFixed(1)}%` : 
              name === 'interventionRate' ? `${value.toFixed(1)}%` : value,
              name === 'averageAccuracy' ? 'Avg Accuracy' :
              name === 'interventionRate' ? 'Intervention Rate' : 'Attempts'
            ]}
          />
          <Legend />
          <Bar 
            dataKey="averageAccuracy" 
            fill={colors.primary}
            name="Accuracy %"
          />
          <Bar 
            dataKey="interventionRate" 
            fill={colors.warning}
            name="Intervention Rate %"
          />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          {type === 'trend' && 'Pronunciation Accuracy Trends'}
          {type === 'distribution' && 'Accuracy Score Distribution'}
          {type === 'words' && 'Most Challenging Words'}
        </h3>
        <p className="text-sm text-gray-600">
          {type === 'trend' && 'Daily pronunciation accuracy and intervention trends'}
          {type === 'distribution' && 'Distribution of pronunciation accuracy scores'}
          {type === 'words' && 'Words requiring the most pronunciation support'}
        </p>
      </div>

      {type === 'trend' && renderTrendChart()}
      {type === 'distribution' && renderDistributionChart()}
      {type === 'words' && renderWordAnalysisChart()}

      {assessments.length === 0 && (
        <div className="flex items-center justify-center h-64 text-gray-500">
          <div className="text-center">
            <InfoIcon className="w-12 h-12 mx-auto mb-4" />
            <p>No pronunciation data available</p>
          </div>
        </div>
      )}
    </div>
  );
}